'use client'

import { useAuthorization } from '@entities/authentication'
import { isKrProviderCancelled, type KrPaymentOrder, type PrepareKrPayment } from '@entities/kr-payment/model'
import { generateMerchantUid } from '@entities/payment'
import { userEntityQueries } from '@entities/users'
import {
  basePaymentRedirectedSearchParamsSchema,
  HandlePaymentQueue,
  LumpSumPaymentRedirectedSearchParamsSchema,
  PaymentActionState,
  PaymentCustomData,
  usePaymentRedirectedSearchParams,
} from '@features/payment'
import { isPaymentCanceled } from '@features/payment/lib/is-payment-canceled'
import { preserveMarketingParams } from '@features/payment/lib/marketing-params'
import { isPaymentError, normalizePortoneCurrency, requestPayment } from '@features/payment/lib/portone-v2'
import {
  preparedCustomData,
  preparedPaymentUrl,
  requestPreparedProvider,
  preparedChannelKey,
  confirmPreparedPayment,
  confirmPreparedPaymentReturn,
} from '@features/payment/lib/prepared-payment'
import { setParams } from '@features/payment/lib/redirect-params'
import { createTossPaymentReturnUrl } from '@features/payment/lib/toss-payment-return'
import { rpc } from '@server/infrastructure/rpc'
import { PaymentMessageBodyType } from '@server/schemas'
import { env, productionAppEnvSchema } from '@shared/config/env'
import { useTranslation } from '@shared/config/i18n'
import { error, success } from '@shared/libs'
import { useNativeBridgeStore } from '@shared/models'
import { openAlertDialog } from '@shared/ui'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

type UsePaymentByTossPaymentsProps = {
  validatePayment: () => boolean
  preparePayment?: PrepareKrPayment
  handlePaymentQueue: HandlePaymentQueue
  merchantName: string
  amount: number
  currency?: string
  customData: PaymentCustomData
  checkCouponIfUseCoupon: () => Promise<void>
  paymentType: Extract<PaymentMessageBodyType, 'LUMP_SUM'>
  billingFailSlackMessage: (errorCode?: string, errorMessage?: string) => string
  paymentWebhookUrl: URL
  mobileRedirectSearchParams: Nullable<
    | Brand<Pick<LumpSumPaymentRedirectedSearchParamsSchema, 'coupon_id'>, 'LUMP_SUM'>
    | Brand<
        Pick<LumpSumPaymentRedirectedSearchParamsSchema, 'zip_code' | 'address' | 'detail_address' | 'coupon_id'>,
        'IPAD'
      >
  >
}

export const usePaymentByTossPayments = ({
  validatePayment,
  preparePayment,
  handlePaymentQueue,
  merchantName,
  amount,
  currency = 'KRW',
  customData,
  checkCouponIfUseCoupon,
  billingFailSlackMessage,
  paymentWebhookUrl,
}: UsePaymentByTossPaymentsProps) => {
  const t = useTranslation()
  const authorization = useAuthorization()
  const { os, from } = useNativeBridgeStore()
  const redirectedSearchParams = usePaymentRedirectedSearchParams()

  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken: authorization.bearerToken }),
  })

  const handlePaymentQueueRef = useRef(handlePaymentQueue)

  // V1/V2 호환 리다이렉트 조건 체크
  const conditionForHandlePaymentQueue = useMemo(() => {
    if (!redirectedSearchParams || isKrProviderCancelled(redirectedSearchParams)) {
      return false
    }

    if (redirectedSearchParams.__brand !== 'LUMP_SUM') {
      return false
    }

    if (
      redirectedSearchParams.pg_provider !==
      basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.TOSS_PAYMENTS
    ) {
      return false
    }

    // V1/V2 호환: imp_uid 또는 payment_id/paymentId 확인
    const hasV1Params = redirectedSearchParams.imp_uid && redirectedSearchParams.merchant_uid
    const hasV2Params = redirectedSearchParams.payment_id || redirectedSearchParams.paymentId

    if (!hasV1Params && !hasV2Params) {
      return false
    }

    if (!redirectedSearchParams.complete || !redirectedSearchParams.amount || !redirectedSearchParams.payment_type) {
      return false
    }

    return true
  }, [redirectedSearchParams])

  useQuery({
    queryKey: ['conditionForHandleTossPaymentsPaymentQueue', redirectedSearchParams?.purchase_order_id ?? 'legacy'],
    queryFn: async () => {
      if (redirectedSearchParams?.__brand !== 'LUMP_SUM' || !redirectedSearchParams) {
        throw new Error(t('payment.errors.unsupportedPaymentMethod'))
      }

      await confirmPreparedPaymentReturn(redirectedSearchParams, authorization.bearerToken)

      // V1/V2 호환 impUid 추출
      const impUid =
        redirectedSearchParams.imp_uid ??
        redirectedSearchParams.tx_id ??
        redirectedSearchParams.payment_id ??
        redirectedSearchParams.paymentId
      const merchantUid =
        redirectedSearchParams.merchant_uid ?? redirectedSearchParams.payment_id ?? redirectedSearchParams.paymentId

      return handlePaymentQueueRef.current({
        status: 'success',
        data: {
          impUid: impUid!,
          amount: redirectedSearchParams.amount,
          merchantUid: merchantUid!,
          customerUid: undefined,
        },
      })
    },
    enabled:
      conditionForHandlePaymentQueue &&
      redirectedSearchParams?.__brand === 'LUMP_SUM' &&
      redirectedSearchParams?.pg_provider ===
        basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.TOSS_PAYMENTS,
    retry: false,
  })

  const createMobileRedirectUrl = (prepared?: KrPaymentOrder | null): URL => {
    if (!prepared) {
      return preserveMarketingParams(createTossPaymentReturnUrl(window.location.href))
    }
    // The frozen order route also handles manual authorization returns without re-reading today's catalogue.
    const url = new URL(`/subscribes/payment/${encodeURIComponent(prepared.subscribeId)}`, window.location.origin)
    setParams(url, {
      payment_type: 'LUMP_SUM',
      complete: 'true',
      amount: String(prepared.paymentAmount),
      pg_provider: basePaymentRedirectedSearchParamsSchema.shape.pg_provider.Values.TOSS_PAYMENTS,
    })
    return preserveMarketingParams(preparedPaymentUrl(url, prepared))
  }

  /**
   * V2 SDK를 사용한 토스페이먼츠 결제
   * V1의 window.IMP.request_pay를 PortOne.requestPayment로 교체
   */
  return async (): Promise<'success' | 'error'> => {
    if (!validatePayment() || !userInfo) {
      return error()
    }

    try {
      await checkCouponIfUseCoupon()
    } catch {
      return error()
    }

    const redirectUrlRequired = ['IOS', 'IPAD', 'ANDROID', 'ANDROID_TABLET'].includes(os ?? '') || from === 'APP'
    const prepared = await preparePayment?.(true)
    if (preparePayment && !prepared) {
      return success()
    }
    const merchantUid = prepared?.orderId ?? generateMerchantUid(userInfo.id)
    const mobileRedirectUrl = createMobileRedirectUrl(prepared)
    const finalAmount = prepared?.paymentAmount ?? amount

    const buyerName = productionAppEnvSchema.safeParse(env.NEXT_PUBLIC_APP_ENV).success
      ? userInfo.name
      : `${userInfo.name}-${env.NEXT_PUBLIC_APP_ENV}`

    // V2 SDK requestPayment 호출
    const response = await requestPreparedProvider(
      () =>
        requestPayment({
          channelKey: preparedChannelKey(prepared, env.NEXT_PUBLIC_IMP_TOSS_PAYMENT_CHANNEL_KEY),
          paymentId: merchantUid,
          orderName: merchantName,
          totalAmount: finalAmount,
          currency: normalizePortoneCurrency(currency),
          payMethod: 'CARD', // 토스페이먼츠 카드 결제
          customer: {
            customerId: userInfo.id.toString(),
            email: userInfo.email,
            fullName: buyerName,
            phoneNumber: userInfo.phone,
          },
          customData: preparedCustomData(customData, prepared) as Record<string, unknown>,
          noticeUrls: [preparedPaymentUrl(paymentWebhookUrl, prepared).toString()],
          redirectUrl: redirectUrlRequired ? mobileRedirectUrl.toString() : undefined,
        }),
      preparePayment,
      prepared?.orderId,
    )

    if (isPaymentCanceled(response)) {
      // 서버가 주문을 취소하거나 승인 여부를 조회하는 기존 흐름은 유지한다.
      await preparePayment?.cancel?.(prepared?.orderId)
      return error()
    }

    if (!response || isPaymentError(response)) {
      if (response) {
        await preparePayment?.cancel?.(prepared?.orderId)
      }
      const errorMessage = response?.message ?? t('payment.alerts.paymentFailed')
      openAlertDialog({ title: errorMessage })
      // Slack 실패 로깅은 사용자 흐름을 막지 않도록 await 하지 않는다
      void rpc.api.v1.payment['logging-payment-success']
        .$post(
          {
            json: {
              action: 'SLACK_FAIL',
              message: billingFailSlackMessage(response?.code, errorMessage),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${authorization.bearerToken}`,
            },
          },
        )
        .catch(() => {})
      return error()
    }

    // V2 response에서 txId를 impUid로 매핑
    await confirmPreparedPayment(prepared, response, authorization.bearerToken)

    const paymentState: PaymentActionState = {
      status: 'success',
      data: {
        impUid: response.txId ?? response.paymentId,
        amount: finalAmount,
        merchantUid: response.paymentId,
        customerUid: undefined, // LUMP_SUM은 customerUid 없음
      },
    }

    await handlePaymentQueue(paymentState)
    return success()
  }
}
